import type { TFunction } from "i18next";

import {
  SalesOrderItemAvailabilitySummaryStatusEnum,
  type SalesOrderItemAvailabilityTotalSummary,
} from "~/api/openapi";

import type {
  SalesOrderStatusTileSummary,
  SalesOrderStatusTileSummaryDict,
} from "../interface/SalesOrderStatusTileSummary";

const emptySummary: Pick<SalesOrderStatusTileSummary, "value" | "totalCost"> = {
  value: 0,
  totalCost: 0,
};

export type OpenOrdersColors = Record<SalesOrderItemAvailabilitySummaryStatusEnum, string>;

export type MapAvailabilitySummaryToPieChartProps = {
  salesOrderItemAvailabilities: SalesOrderItemAvailabilityTotalSummary[] | undefined;
  colors: OpenOrdersColors | undefined;
  t: TFunction;
};

export const mapAvailabilitySummaryToPieChart = (
  props: MapAvailabilitySummaryToPieChartProps,
): SalesOrderStatusTileSummaryDict => {
  const { salesOrderItemAvailabilities, colors, t } = props;

  const dict: SalesOrderStatusTileSummaryDict = {
    [SalesOrderItemAvailabilitySummaryStatusEnum.InStock]: {
      ...emptySummary,
      id: t(`salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InStock}`),
      label: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InStock}`,
      ),
      linkId: SalesOrderItemAvailabilitySummaryStatusEnum.InStock,
      color: colors?.IN_STOCK ?? "",
    },
    [SalesOrderItemAvailabilitySummaryStatusEnum.InStockOverdue]: {
      ...emptySummary,
      id: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InStockOverdue}`,
      ),
      label: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InStockOverdue}`,
      ),
      linkId: SalesOrderItemAvailabilitySummaryStatusEnum.InStockOverdue,
      color: colors?.IN_STOCK_OVERDUE ?? "",
    },
    [SalesOrderItemAvailabilitySummaryStatusEnum.InStockNotReleased]: {
      ...emptySummary,
      id: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InStockNotReleased}`,
      ),
      label: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InStockNotReleased}`,
      ),
      linkId: SalesOrderItemAvailabilitySummaryStatusEnum.InStockNotReleased,
      color: colors?.IN_STOCK_NOT_RELEASED ?? "",
    },
    [SalesOrderItemAvailabilitySummaryStatusEnum.Incoming]: {
      ...emptySummary,
      id: t(`salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.Incoming}`),
      label: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.Incoming}`,
      ),
      linkId: SalesOrderItemAvailabilitySummaryStatusEnum.Incoming,
      color: colors?.INCOMING ?? "",
    },
    [SalesOrderItemAvailabilitySummaryStatusEnum.InPreorder]: {
      ...emptySummary,
      id: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InPreorder}`,
      ),
      label: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.InPreorder}`,
      ),
      linkId: SalesOrderItemAvailabilitySummaryStatusEnum.InPreorder,
      color: colors?.IN_PREORDER ?? "",
    },
    [SalesOrderItemAvailabilitySummaryStatusEnum.OnRequest]: {
      ...emptySummary,
      id: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.OnRequest}`,
      ),
      label: t(
        `salesOrderAvailabilityStatus.${SalesOrderItemAvailabilitySummaryStatusEnum.OnRequest}`,
      ),
      linkId: SalesOrderItemAvailabilitySummaryStatusEnum.OnRequest,
      color: colors?.ON_REQUEST ?? "",
    },
  };

  if (salesOrderItemAvailabilities) {
    for (const summary of salesOrderItemAvailabilities) {
      dict[summary.status] = {
        id: t(`salesOrderAvailabilityStatus.${summary.status}`),
        label: t(`salesOrderAvailabilityStatus.${summary.status}`),
        linkId: summary.status,
        color: colors?.[summary.status] ?? "",
        value: summary.totalQuantity,
        totalCost: summary.totalAmount,
      };

      if (summary.totalQuantity > 0) {
        dict[summary.status].value = summary.totalQuantity;
        dict[summary.status].totalCost = summary.totalAmount;
      }
    }
  }

  return dict;
};
